// The New Grad List — role config + data snapshot (see README.md for the shape).
// 2027 new-grad cycle: every figure traces to a posted salary range, a live
// careers page, levels.fyi, or a dated public source. Verify against live
// postings when refreshing: new-grad reqs open and close within weeks.
//
// To refresh: update lastUpdated, re-verify baseSalary/totalComp + hiringStatus
// + timeline against each applyUrl, and adjust scores (0–10) if a program's
// trajectory changed. Ranks and tiers are editorial; keep tiers contiguous.
//
// Snapshot: October 10, 2026. "Verified" below means read from the company's
// own req or careers page on that date; total comp figures are levels.fyi
// entry-level ranges (10th–90th percentile) unless noted, and the interview
// loops are candidate-reported (no company on this list publishes its loop).

import {fmtRange} from './format';
import jobs from './data/new-grad.json';

const CYCLE = '2027';

const newGradJobs = {
  lastUpdated: '2026-10-10',
  jobs,
};

const openCount = jobs.filter((job) => job.activelyHiring).length;
const topOf = (key) => jobs
  .filter((job) => job[key])
  .reduce((max, job) => Math.max(max, job[key].max), 0);
const topBase = topOf('baseSalary');
const topTotal = topOf('totalComp');

export const marketStats = [
  {value: `${jobs.length}`, label: 'Programs ranked'},
  {value: `${openCount}`, label: 'Accepting applications'},
  {value: fmtRange({min: topBase, max: topBase}), label: 'Highest posted base'},
  {value: fmtRange({min: topTotal, max: topTotal}), label: 'Top-of-band total comp'},
];

export const methodology = [
  {
    weight: '30%',
    name: 'Compensation',
    description: 'Posted new-grad base bands, levels.fyi entry-level data, and verified offer reports — total comp where equity data exists, base bands otherwise.',
  },
  {
    weight: '25%',
    name: 'Program maturity',
    description: 'How real the new-grad program is: a dedicated req versus a generic junior opening, cohort structure, mentorship and onboarding, and how team matching works.',
  },
  {
    weight: '25%',
    name: 'Career capital',
    description: 'What the first job does for the rest of your career — brand strength, alumni outcomes, and the caliber of problems new grads actually get to own.',
  },
  {
    weight: '20%',
    name: 'Momentum',
    description: `Req counts, how early and how widely the ${CYCLE} window opened, and whether the program is growing, shrinking, or late relative to its own history.`,
  },
];

export const excludedNote = 'We also track Anthropic, Netflix, Citadel, Ramp, and Cursor, currently outside the ranking. Anthropic has no new grad software engineer req or program — its careers FAQ says it doesn’t offer internships, and early-career hiring runs through the Fellows research program and Claude Corps; the other four are queued for the next data pass. Meta stays ranked despite having no live Software Engineer (University Grad) req because the track has run every prior cycle; Databricks likewise, with its 2027 req expected but not yet posted.';

export const faqItems = [
  {
    question: 'When do new grad software engineer applications open?',
    answer: 'Most 2027 reqs opened between July and early October 2026: Jane Street posted its New York new grad req on July 6, Stripe on August 31, OpenAI on September 14, and Amazon’s 2027 Early Career reqs on September 24 and October 1. Palantir keeps evergreen reqs open year-round. Two big names were late — Databricks (which opened on August 8 last cycle) and Meta had no 2027 new grad SWE req posted as of October 10 — and Google’s BS/MS campus req came and went within weeks. Nearly every program hires on a rolling basis, so apply the week a req opens.',
  },
  {
    question: 'How much do new grad software engineers make?',
    answer: 'Posted 2027 new grad base bands run from about $122k (Stripe’s $122,100–$134,400 band and Amazon’s $122,300 Nashville starting rate) to Jane Street’s flat $300,000, with most big-tech bands between $120k and $175k. Equity and sign-on bonuses do the rest: levels.fyi puts estimated first-year total compensation at roughly $150k–$215k at Amazon, Microsoft and Apple, $200k–$270k at Stripe, Palantir, Databricks and OpenAI, and $250k–$490k at Jane Street.',
  },
  {
    question: 'What is the new grad software engineer interview process like?',
    answer: 'Almost every program runs the same shape: an online assessment (HackerRank, CodeSignal or Codility, 60–90 minutes), a 45–60 minute technical phone screen, then a virtual or on-site loop of three to five rounds that is mostly coding, with a light system design round at some companies and a behavioral round everywhere. The variations are what you prepare for: Stripe’s loop works inside an unfamiliar codebase (Bug Squash and Integration rounds), Palantir runs a decomposition round, Meta puts an AI assistant inside CoderPad, and Jane Street’s final round is in person. Expect three to seven weeks end to end.',
  },
  {
    question: 'Do I need an internship to get a new grad offer?',
    answer: 'No, but it helps more than anywhere else in the market. NVIDIA calls internships its primary pipeline for new college grads, Stripe describes its pipeline as intern-first with selective conversion, and third-party guides report that Microsoft fills many seats with returning interns by November. Most programs on this list also post direct new grad reqs — Apple and Microsoft are the exceptions, hiring entry-level engineers through standard junior reqs instead — and Jane Street states outright that no prior OCaml, finance or specific degree is required.',
  },
  {
    question: 'Which companies pay new grad software engineers the most?',
    answer: 'On posted base, Jane Street ($300,000 flat) leads by a wide margin, followed by OpenAI ($180,000 flat), Google’s PhD 2027 reqs ($147,000–$210,000), and Palantir ($145,000–$155,000). On estimated total compensation, Jane Street ($250k–$490k), OpenAI ($245k–$270k), Databricks ($230k–$262k) and Palantir ($190k–$267k) top the list, per levels.fyi entry-level data.',
  },
  {
    question: 'What counts as a new grad?',
    answer: 'Each program defines it slightly differently. Stripe wants a degree obtained by summer 2027 and no more than 18 months of professional experience; Palantir takes Fall 2026 or Spring 2027 graduates, or anyone with under six months of industry experience; OpenAI’s req asks for 0–1 years of experience; Amazon’s 2027 Annapurna req asks for a final graduation date of May 2027 or later. Apple, Microsoft and NVIDIA post no graduation window at all, because they hire entry-level engineers into standard junior reqs.',
  },
  {
    question: 'Which companies have no dedicated new grad program?',
    answer: 'Apple hires entry-level engineers team by team into ICT2 reqs, Microsoft hires into standard IC2 Software Engineer reqs and auto-enrolls qualifying hires in its Aspire program, and Anthropic has no new grad SWE path at all — its early-career routes are the Fellows research program and Claude Corps. Meta’s Software Engineer (University Grad) req, a dedicated track in prior years, was not posted as of October 10, 2026.',
  },
];

const inCities = (job, cities) => cities.some((city) => job.locations.join(' · ').includes(city));

// Collection sub-pages (/jobs/new-grad/<slug>). Each targets a long-tail query;
// `matches` selects which programs appear. The `salary` collection renders the
// full comp table instead of a filtered list.
export const collections = [
  {
    slug: 'salary',
    name: 'Salary guide',
    kind: 'salary',
    title: `New Grad Software Engineer Salary ${CYCLE}: Posted Starting Pay at ${jobs.length} Companies`,
    metaDescription: `New grad software engineer salary data for the ${CYCLE} cycle: posted base bands from $122k to $300k, estimated total compensation from $150k to $490k, and where every figure comes from.`,
    h1: 'New grad software engineer salary, in real numbers',
    intro: 'Posted 2027 new grad base bands start near $122k (Stripe, and Amazon’s Nashville rate) and top out at Jane Street’s flat $300,000, with most big-tech bands between $120k and $175k. Equity and sign-on bonuses do the rest: estimated first-year total compensation runs roughly $150k–$215k at Amazon, Microsoft and Apple, $200k–$270k at Stripe, Palantir, Databricks and OpenAI, and $250k–$490k at Jane Street. Every band below comes from a posted range, levels.fyi, or a dated public source.',
    matches: () => true,
    faqItems: [{
      question: 'What is the average new grad software engineer salary?',
      answer: 'Across the programs tracked here, posted base bands cluster between $120k and $175k. levels.fyi entry-level total-compensation medians run from about $161k (Microsoft level 59, Apple ICT2) through $176k–$178k (Amazon SDE I, NVIDIA IC1, Meta E3) and $204k (Google L3) to $225k–$247k (Stripe L1, Palantir, Databricks L3), with Jane Street’s L1 median at $339k.',
    }, {
      question: 'Which company pays new grads the most?',
      answer: 'Jane Street, by a wide margin: a flat $300,000 base plus a discretionary bonus, with levels.fyi reporting $250k–$490k in first-year total compensation. Among tech companies, OpenAI’s flat $180,000 base and Google’s $147,000–$210,000 PhD band lead on base, and Databricks ($230k–$262k) and OpenAI ($245k–$270k) lead on estimated total compensation.',
    }, {
      question: 'Do new grad offers include equity?',
      answer: 'At every program on this list except Jane Street (which pays a discretionary bonus instead), yes: Stripe, Palantir, Databricks, Google, Amazon, Microsoft, Apple, NVIDIA and OpenAI all grant equity to new grads. levels.fyi data shows stock adding roughly $30k–$75k a year at the top programs — about $33k at Amazon and about $75k at OpenAI.',
    }, {
      question: 'Is a posted salary range the same as a new grad offer?',
      answer: 'Not always. Stripe, Palantir, OpenAI and Jane Street post new-grad-specific figures. Apple, Microsoft and NVIDIA post the full junior-level band (ICT2, IC2, Level 1–2), which spans several years of experience, so new grad offers usually land in the lower half; Amazon posts a single starting pay per location rather than a range.',
    }],
  },
  {
    slug: 'timeline-2027',
    name: '2027 timeline',
    kind: 'list',
    title: `New Grad Software Engineer ${CYCLE} Timeline: When Applications Open and Close`,
    metaDescription: `When ${CYCLE} new grad software engineer applications open and close at ${jobs.length} top programs — posting dates, rolling deadlines, and which reqs are already gone.`,
    h1: 'The 2027 new grad application timeline',
    intro: 'New grad hiring is a calendar, not a job board. For the 2027 cycle, Jane Street opened first (July 6), Palantir never closes, Stripe posted on August 31, OpenAI on September 14, Amazon’s 2027 reqs on September 24 and October 1, and NVIDIA posts software reqs with windows as short as four days. Google’s BS/MS campus req came and went within weeks, and Databricks and Meta had not posted at all by October 10. Every program below lists its posting date and whether it is rolling or deadline-driven.',
    matches: () => true,
    faqItems: [{
      question: 'When should I apply for 2027 new grad software engineer jobs?',
      answer: 'The week a req opens. Every program on this list hires on a rolling basis, Stripe and Jane Street say so explicitly, and third-party guides report that programs like Microsoft fill most seats by November. For the 2027 cycle that meant July (Jane Street), late August (Stripe, Google’s PhD reqs), September (OpenAI, Amazon) and October (NVIDIA’s latest software reqs).',
    }, {
      question: 'Are new grad applications rolling or deadline-based?',
      answer: 'Rolling, almost without exception. Only NVIDIA states per-req closing dates (‘accepted at least until’ a date, sometimes just four days out); Stripe, Jane Street, Palantir, OpenAI, Amazon, Google, Microsoft and Apple post no deadline and close reqs when they fill — Google’s BS/MS campus req disappeared within weeks of posting.',
    }, {
      question: 'Which 2027 new grad reqs have already closed?',
      answer: 'As of October 10, 2026: Google’s BS/MS ‘Software Engineer, Early Career, Campus’ req (posted in August) is gone, leaving only PhD tracks; NVIDIA’s earlier software reqs closed on June 7, July 26 and September 5; and Stripe’s 2026-cycle req closed when the 2027 req replaced it. OpenAI’s single req is live but last cycle’s was gone by September 30.',
    }, {
      question: 'Is there a second new grad hiring round in the spring?',
      answer: 'Sometimes. NVIDIA keeps posting software new-college-grad reqs through spring 2027, Palantir and Amazon run evergreen reqs, and third-party guides describe a smaller January–March round at Microsoft for leftover headcount. The flagship programs (Stripe, Jane Street, Databricks) are effectively one-window hires.',
    }],
  },
  {
    slug: 'new-york',
    name: 'New York',
    kind: 'list',
    title: `New Grad Software Engineer Jobs in New York (${CYCLE})`,
    metaDescription: `Every major new grad software engineer program hiring in New York City for ${CYCLE} — posted starting pay and application windows.`,
    h1: 'New grad software engineer jobs in New York',
    intro: 'New York is where the top of this list hires: Jane Street’s $300,000-base new grad role is NYC-only, Palantir runs four of its ten US new grad reqs out of New York, Stripe’s single US req covers New York alongside Seattle and South San Francisco, and Amazon posts a $141,600 New York starting rate. Google and Microsoft also list New York among their early-career sites.',
    matches: (job) => inCities(job, ['New York']),
    faqItems: [{
      question: 'Which companies hire new grad software engineers in New York?',
      answer: 'Jane Street (NYC-only), Palantir (four New York reqs), Stripe, Amazon, Google and Microsoft all hire 2027 new grads into New York. The AI-native programs skew west: OpenAI’s single new grad req is San Francisco only, and Databricks hires into the Bay Area and Bellevue.',
    }, {
      question: 'How much do new grad software engineer jobs in New York pay?',
      answer: 'Jane Street posts a flat $300,000 base; Palantir posts $145,000–$155,000 on every US req including New York; Stripe’s US band is $122,100–$134,400; and Amazon’s New York starting pay is $141,600. Google’s campus band ($123,000–$174,000) and Microsoft’s NYC-adjusted IC2 band ($111,100–$183,700) applied across their US sites.',
    }, {
      question: 'Are New York new grad roles in office?',
      answer: 'Mostly. Jane Street is in office, Stripe expects at least half of each month in the office, Palantir is office-first with hybrid flexibility on many teams, and Amazon is five days a week. Google and Microsoft are hybrid.',
    }],
  },
  {
    slug: 'bay-area',
    name: 'Bay Area',
    kind: 'list',
    title: `New Grad Software Engineer Jobs in the Bay Area (${CYCLE})`,
    metaDescription: `Every major new grad software engineer program hiring in San Francisco and Silicon Valley for ${CYCLE} — posted starting pay and application windows.`,
    h1: 'New grad software engineer jobs in the Bay Area',
    intro: 'The Bay Area has the most new grad seats on this list: Google (Mountain View, Sunnyvale, San Bruno), Apple (Cupertino, Sunnyvale, Santa Clara), NVIDIA (Santa Clara), Databricks (San Francisco, Mountain View), OpenAI (San Francisco only), Palantir (Palo Alto), Stripe (South San Francisco HQ), Amazon (Cupertino, Sunnyvale, San Francisco), Microsoft (Mountain View) and Meta (Menlo Park). Posted Bay Area pay runs from Amazon’s $148,100 Cupertino starting rate to OpenAI’s flat $180,000 and Apple’s $129,300–$194,700 ICT2 band.',
    matches: (job) => inCities(job, ['San Francisco', 'Mountain View', 'Sunnyvale', 'San Bruno', 'Cupertino', 'Santa Clara', 'Palo Alto', 'Menlo Park']),
    faqItems: [{
      question: 'Which companies hire new grad software engineers in the Bay Area?',
      answer: 'Ten of the eleven programs on this list: Google, Apple, NVIDIA, Databricks, OpenAI, Palantir, Stripe, Amazon, Microsoft and Meta. Jane Street is the exception — its new grad software role is New York only.',
    }, {
      question: 'How much do Bay Area new grad software engineer jobs pay?',
      answer: 'Posted figures as of October 2026: OpenAI $180,000 flat; Apple $129,300–$194,700 (full ICT2 band); NVIDIA $108,000–$195,500 across Levels 1–2; Amazon $148,100 in Cupertino; Palantir $145,000–$155,000; Stripe $122,100–$134,400; and Google’s campus band of $123,000–$174,000. Estimated total compensation runs highest at OpenAI ($245k–$270k) and Databricks ($230k–$262k).',
    }, {
      question: 'Do Bay Area new grad roles require being in the office?',
      answer: 'Nearly all are hybrid or in office. Amazon is five days a week, Databricks hires into its hub offices, OpenAI runs three days a week in San Francisco, Stripe expects at least half of each month in office, and Google, Apple and Microsoft are hybrid. None of the Bay Area new grad reqs on this list is fully remote.',
    }],
  },
];

// Company-page FAQ (also emitted as FAQPage JSON-LD).
const companyFaqItems = (job, updatedLabel) => {
  const items = [];

  if (job.baseSalary || job.totalComp) {
    items.push({
      question: `How much does ${job.company} pay new grad software engineers?`,
      answer: `${job.baseSalary ? `${job.company}’s posted new-grad base band is ${fmtRange(job.baseSalary)}.` : ''}${job.totalComp ? ` Estimated total compensation including equity and sign-on is ${fmtRange(job.totalComp)}.` : ''} ${job.compNote}`.trim(),
    });
  } else {
    items.push({
      question: `How much does ${job.company} pay new grad software engineers?`,
      answer: `${job.company} doesn’t publish a salary range for this role. ${job.compNote}`,
    });
  }

  items.push({
    question: `Is ${job.company} hiring new grad software engineers for ${CYCLE}?`,
    answer: `As of ${updatedLabel}: ${job.hiringStatus}. Check the live careers page for current reqs.`,
  });

  items.push({
    question: `When do ${job.company} new grad applications open?`,
    answer: job.timeline,
  });

  items.push({
    question: `Who is eligible for the ${job.company} new grad program?`,
    answer: job.experience,
  });

  return items;
};

// Role config consumed by JobsListPage. Strings here are the page copy; the
// components only add layout and the shared boilerplate.
const newGrad = {
  slug: 'new-grad',
  analyticsId: 'new-grad-jobs', // tracker page ids and ?src= values
  listName: 'The New Grad List',
  roleName: 'New Grad Software Engineer',
  roleShort: 'new grad', // mid-sentence: "Google new grad salary"
  roleShortTitle: 'New Grad', // title case: breadcrumbs, link labels
  breadcrumbName: 'New Grad Jobs',
  socialImage: '/images/aced_social_thumbnail.png',
  courseHref: '/courses/software-engineering',
  questionsHref: '/questions',
  experienceLabel: 'Eligibility',
  tierTaglines: {
    S: 'The flagship programs — structured cohorts, top-of-market pay, and a brand that compounds',
    A: 'The scaled hirers — large cohorts, proven onboarding, and reqs across many offices',
    B: 'The rising programs — smaller cohorts, faster ownership, and AI-native trajectories',
  },
  filters: [{
    name: 'All programs',
    value: 'all',
    predicate: () => true,
  }, {
    name: 'Applications open',
    value: 'hiring',
    predicate: (job) => job.activelyHiring,
  }, {
    name: 'Posts a salary band',
    value: 'posted',
    predicate: (job) => Boolean(job.baseSalary),
  }, {
    name: 'Dedicated new grad track',
    value: 'dedicated',
    predicate: (job) => job.newGradReq,
  }],
  hub: {
    title: `New Grad Software Engineer Jobs ${CYCLE}: ${jobs.length} Programs, Ranked`,
    // The page appends "Last verified <date>."
    metaDescription: `A living, ranked index of new grad software engineer jobs — posted starting salaries, application windows, and hiring status at ${jobs.length} top programs for the ${CYCLE} cycle.`,
    socialDescription: 'Every top new grad software engineer program, ranked — starting pay and application windows.',
    h1: 'The best new grad software engineer programs, ranked.',
    intro: `New grad hiring runs on a calendar: most ${CYCLE} reqs open between August and October and fill on a rolling basis. This is the living index of the programs worth your application — posted starting pay and when each window opens and closes, re-verified as the cycle moves. Every number traces to a posted range, a live careers page, or levels.fyi.`,
    sourcesNote: 'Sources: posted salary ranges, levels.fyi, and company careers pages.',
    compIntro: 'Posted base-salary bands for each program’s new grad software engineer req, US market — equity, sign-on, and bonus come on top, and at the top programs they add $50k or more in year one. Bands are entry-level; competing offers land near the top.',
    about: {
      heading: 'What is a new grad software engineer program?',
      paragraphs: [
        'A new grad program is a company’s dedicated hiring track for engineers finishing a bachelor’s or master’s degree — usually a single req titled “Software Engineer, New Grad” or “Early Career,” with its own recruiters, a fixed eligibility window (graduation within roughly a year of the start date), and an interview loop tuned for candidates without full-time experience. Most open between August and October for starts the following summer, and the best ones fill on a rolling basis long before any posted deadline.',
        'The programs differ more than the titles suggest. Some run structured cohorts with mentors, onboarding buddies, and team matching after you join; others are evergreen reqs that hire whenever a strong candidate shows up. Starting pay diverges too: posted base bands cluster between $120k and $175k, but equity and sign-on bonuses push total compensation at the top programs well past $200k.',
      ],
      guidesLead: 'Preparing for the loop? Start with our guides:',
      guides: [
        {label: 'the Google new grad interview guide', href: '/guides/google-software-engineer-new-grad-interview'},
        {label: 'the OpenAI new grad interview guide', href: '/guides/openai-software-engineer-new-grad-interview'},
      ],
    },
  },
  courseCallout: {
    title: 'Interviewing for new grad SWE roles?',
    // Rendered after "<Brand>’s"
    body: 'Software Engineering interview course covers the coding rounds, the system design basics new grads get asked, and the behavioral interview.',
  },
  finalCta: {
    body: 'Practice coding interview questions, mock interview with peers, and learn the frameworks hiring managers actually score you on.',
    courseLabel: 'Take the SWE course',
  },
  companyPage: {
    title: (job) => `${job.company} New Grad Software Engineer: Salary, Open Roles & ${CYCLE} Timeline`,
    metaDescription: (job, comp, updatedLabel) => `${job.company} new grad software engineer salary${comp.value !== '—' ? ` (${comp.value} ${comp.label.replace(' (est.)', '')})` : ''}, application window, locations, and hiring status for ${CYCLE}. Verified ${updatedLabel}.`,
  },
  companyFaqItems,
  lastUpdated: newGradJobs.lastUpdated,
  jobs,
  collections,
  faqItems,
  marketStats,
  methodology,
  excludedNote,
};

export default newGrad;
